<script setup>
import BiColorRangePicker from '~/bi/components/chart-builder/common/bi-color-range-picker.vue';

const props = defineProps({
  formInstance: {
    type: Object,
    required: true,
  },
  mapColumns: {
    type: Array,
  },
});

const default_heatmap_options = {
  color: '#000000',
  color_range: 5,
  start_range_at: 'min',
  start_range_value: '',
  end_range_at: 'max',
  end_range_value: '',
};
const state = reactive({
  is_advanced_options: true,
});

const form_data = computed(() => props.formInstance?.data);

function updateHeatmapOptions(data) {
  props.formInstance.elements$.heatmap_options.update({ ...(form_data.value?.heatmap_options || {}), ...data });
}
</script>

<template>
  <div class="space-y-6">
    <SelectElement
      name="map_type"
      :label="$t('Visualization')"
      :items="{
        default: $t('Default'),
        cluster: $t('Cluster'),
        heatmap: $t('Heatmap'),
      }"
      default="default"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />

    <SelectElement
      name="feature_style"
      :label="$t('Style')"
      :items="{
        color: $t('Color'),
        icon: $t('Icon'),
      }"
      :conditions="[['map_type', '==', 'default']]"
      default="color"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
    />

    <template v-if="(form_data?.map_type === 'default' && form_data?.feature_style === 'color') || form_data?.map_type === 'cluster'">
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="form_data?.feature_color || '#ff3b3b'"
            @color-selected="props.formInstance.update({ feature_color: $event })"
          />
        </div>
      </div>
    </template>

    <template v-if="form_data?.map_type === 'default' && form_data?.feature_style === 'icon'">
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Icon') }}
        </div>
        <div class="col-span-8">
          <BiIconPicker
            type="outlined"
            :active-icon="form_data?.feature_icon || 'asset'"
            @icon-selected="props.formInstance.update({ feature_icon: $event })"
          />
        </div>
      </div>
    </template>

    <SliderElement
      name="feature_point_size"
      :label="$t('Size')"
      :min="1"
      :max="15"
      :step="0.1"
      :default="6"
      :format="v => `${v}`"
      tooltip-position="bottom"
      :conditions="[['feature_style', '==', 'color'], ['map_type', '==', 'default']]"
    />

    <template v-if="form_data?.map_type === 'cluster'">
      <div class="flex justify-between">
        <div class="col-span-4 text-sm font-medium text-gray-700">
          {{ $t('Cluster color') }}
        </div>
        <div class="col-span-8">
          <BiColorPicker
            type="outlined"
            :active-color="form_data?.feature_cluster_color || '#51bbd6'"
            @color-selected="props.formInstance.update({ feature_cluster_color: $event })"
          />
        </div>
      </div>
    </template>

    <SliderElement
      name="feature_icon_size"
      :label="$t('Size')"
      :min="1"
      :max="6"
      :step="0.1"
      :default="1"
      :format="v => `${v}`"
      tooltip-position="bottom"
      :conditions="[['feature_style', '==', 'icon'], ['map_type', '==', 'default']]"
    />

    <GroupElement
      name="prefix_suffix"
      :conditions="[['map_type', '==', 'cluster']]"
    >
      <div class="col-span-12 flex gap-3">
        <TextElement
          name="prefix"
          :label="$t('Prefix')"
          :placeholder="$t('Enter prefix')"
          class="w-full"
        />
        <TextElement
          name="suffix"
          :label="$t('Suffix')"
          :placeholder="$t('Enter suffix')"
          class="w-full"
        />
      </div>
    </GroupElement>

    <SelectElement
      name="show_labels"
      :label="$t('Show labels')"
      :items="{
        true: $t('Show'),
        false: $t('Hide'),
      }"
      default="true"
      :native="false"
      :can-clear="false"
      :can-deselect="false"
      :conditions="[['map_type', '==', 'cluster']]"
    />

    <ObjectElement
      name="heatmap_options"
      :conditions="[['map_type', '==', 'heatmap']]"
      :default="default_heatmap_options"
    >
      <div class="col-span-12">
        <div class="flex items-center justify-between mb-4">
          <div>
            {{ $t('Inverse colors') }}
          </div>
          <ToggleElement
            name="inverse_colors"
            :default="false"
          />
        </div>
        <BiColorRangePicker
          :form="form_data?.heatmap_options || default_heatmap_options"
          hide-color-picker
          @update-color-range="updateHeatmapOptions({ color_range: $event })"
          @update-color="updateHeatmapOptions({ color: $event })"
        />
        <HiddenElement name="color_range" :default="5" />
        <HiddenElement name="color" default="#000000" />
      </div>
    </ObjectElement>

    <div v-show="form_data?.map_type !== 'heatmap'" class="flex flex-col gap-y-3">
      <div class="cursor-pointer -ml-2" @click="state.is_advanced_options = !state.is_advanced_options">
        <div class="flex items-center text-sm font-semibold text-primary-700">
          <IconHawkChevronRight v-if="!state.is_advanced_options" class="w-6 h-6" />
          <IconHawkChevronDown v-else class="w-6 h-6" />
          {{ $t('Advanced options') }}
        </div>
      </div>
      <SelectElement
        v-show="state.is_advanced_options"
        name="feature_popup_label"
        :label="$t('Label')"
        class="!mt-2"
        :items="mapColumns?.all_columns || []"
        :placeholder="$t('Select')"
        value-prop="alias"
        :native="false"
      />

      <TagsElement
        v-show="state.is_advanced_options"
        name="feature_properties"
        :label="$t('Properties')"
        :placeholder="$t('Select')"
        value-prop="alias"
        :items="mapColumns?.all_columns || []"
        :search="true"
        :native="false"
        :close-on-select="false"
      />
    </div>

    <HiddenElement name="feature_color" />
    <HiddenElement name="feature_icon" />
    <HiddenElement name="feature_cluster_color" />
  </div>
</template>
